import {
  FEATURE_FLAG_META_BY_KEY,
  KNOWN_FEATURE_FLAG_KEYS,
  STAFF_OVERRIDE_FLAGS,
  type FeatureFlagMap,
} from "encamp-shared/src/featureFlags";
import { createContext, useContext, useMemo } from "react";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

/** A single tenant's feature-flag config row (key → enabled). */
type TenantConfig = { key: string; value: boolean };

/**
 * Build the resolved flag map from a tenant's configs, applying flag defaults and the
 * staff-override flags. Shared by useFeatureFlags and the tenant-scoped provider so
 * both compute flags identically.
 */
export function buildFeatureFlagMap(
  tenantConfigs: readonly TenantConfig[] | undefined,
  isStaff: boolean
): FeatureFlagMap {
  const valueByKey = new Map((tenantConfigs ?? []).map((config) => [config.key, config.value]));
  const flagMap = Object.fromEntries(
    KNOWN_FEATURE_FLAG_KEYS.map((key) => [
      key,
      valueByKey.get(key) ?? FEATURE_FLAG_META_BY_KEY[key].defaultValue,
    ])
  ) as FeatureFlagMap;
  if (isStaff) {
    for (const flag of STAFF_OVERRIDE_FLAGS) {
      flagMap[flag] = true;
    }
  }
  return flagMap;
}

/**
 * Override the ambient (current-tenant) flags for a subtree. Used by the staff Manifest
 * Review queue, which mounts customer manifest UIs for OTHER tenants and must render
 * their flags — not the staff user's current/session tenant. Null = no override.
 */
export const FeatureFlagOverrideContext = createContext<FeatureFlagMap | null>(null);

export function useFeatureFlags(): {
  featureFlags: FeatureFlagMap;
  loading: boolean;
} {
  const override = useContext(FeatureFlagOverrideContext);
  const { tenant, loading: tenantLoading } = useTenant();
  const { isStaff, loading: userLoading } = useCurrentUser();

  const featureFlags = useMemo(
    () => buildFeatureFlagMap(tenant?.tenantConfigs, isStaff),
    [tenant?.tenantConfigs, isStaff]
  );

  if (override) return { featureFlags: override, loading: false };
  return { featureFlags, loading: tenantLoading || userLoading };
}
